import { AutocompleteChangeReason } from "@mui/material";
import JsonResponse from "csi/comm/JsonResponse";
import PostRequest from "csi/comm/PostRequest";
import { ILocaleProvider } from "designSystem/locale";
import { ComponentItem } from "designSystem/types/component-item-types";
import { EMPTY_URL } from "csi/constants";
import locale from "csi/locale";
import { getSearch, runSearch } from "csi/service/dataStoreService";
import { resolveNodes } from "csi/service/nodeService";
import { ReactionStore } from "csi/utility/mobxUtility";
import WorkingCache from "csi/state/WorkingCache";
import { sessionStore } from "csi/state/rootStore";
import { C8Node, cnl } from "csi/typings";
import { pathCreate } from "csi/utility/pathUtility";
import xml from "csi/xml";
import Search from "csi/xml/Search";
import * as _ from "lodash";
import { Dictionary } from "lodash";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import React, { ChangeEvent, createContext, useContext } from "react";
import {
	APPROVER,
	DUE_DATE_NONE,
	DUE_DATE_OPTIONS,
	DUE_DATE_SPECIFIC_DATE,
	DUPLICATE_USER_ERROR_RES,
	EMAIL_NOTIFICATION_DEFAULT_OPTION_NAME,
	EMAIL_NOTIFICATION_OPTIONS_QUERY,
	FINAL_DECISION_APPROVER_RESOURCE,
	FINAL_STAGE_DECISION_CALCULATION_ALL_DECISIONS,
	FINAL_STAGE_DECISION_CALCULATION_DECISION_MADE_BY,
	FINAL_STAGE_DECISION_CALCULATION_OPTION_QUERY,
	LOCK_STAGE_NEVER,
	LOCK_STAGE_OPTIONS_QUERY,
	PERMISSION_REQUIRED_ERROR_RES,
	PROOF_DECISION_STATUS,
	PROOF_PERMISSIONS,
	REQUIRED_USER_ERROR_RES,
	REVIEWER,
	STAGE_COMMENTS_OPTIONS_QUERY,
	STAGE_COMMENTS_PUBLIC_ONLY,
	STAGE_TRIGGER,
	STAGE_TRIGGER_OPTIONS,
	STAGE_TRIGGER_TYPE,
	STAGE_TRIGGER_TYPE_OPTIONS_QUERY,
	USER_WITH_SAME_EMAIL_ERROR_RES,
	USE_CUSTOM,
	USE_TEMPLATE,
	VIEWER,
	calculateDueDate,
	fetchIsApprovedWithChangesDisabled,
	fetchIsUserProofAdmin,
	fetchUsersWithProofingRoles,
	isImmediately
} from "../ProofService";

export class Reviewer {
	/** indicate if disable reviewer or not */
	disabled?: boolean;

	/** error resource */
	@observable errorRes?: string;

	/** indicate if this reviewer is inactive */
	isInactive?: boolean;

	/** id */
	id?: string;

	/** indicates if this reviewer is the owner */
	@observable isOwner?: boolean;

	/** name of the reviewer */
	name: string;

	/** permission */
	@observable permission: string;

	/** the resource of potential permission error */
	permissionErrorRes?: string;

	/** preference for email notification */
	proofingEmailNotificationPreference?: EmailNotification;

	/** the role of name of this reviewer */
	role: string;

	/** url of the role */
	roleUrl: cnl;

	/** url of the reviewer */
	@observable url: cnl;

	/** number of users matching the role */
	@observable userMatches?: number;

	/** retrieve the error text res */
	get errorTextRes(): string {
		return this.isDuplicate ? DUPLICATE_USER_ERROR_RES : this.errorRes || this.permissionErrorRes;
	}

	/** indicates if this reviewer has an error */
	get hasError(): boolean {
		return this.isInactive || this.isDuplicate || !!this.errorRes;
	}

	/** indicates if this reviewer is the sole owner */
	get isDuplicate(): boolean {
		return this.stage.reviewers.filter((r) => r.url === this.url).length > 1;
	};

	/** indicates if this reviewer is the sole owner */
	get isSoleOwnerOnProof(): boolean {
		const { proofOwner, stages } = this.proofingStore;
		return this.isOwner && _.flatten(stages.map((s) => s.reviewers.filter((r) => r.url === proofOwner))).length === 1;
	};

	/** indicates if this reviewer user is disabled */
	get isUserSelectionDisabled(): boolean {
		return this.disabled || this.isSoleOwnerOnProof;
	}

	/** indicates if this reviewer delete button is disabled */
	get isUserDeletionDisabled(): boolean {
		return this.disabled || this.stage.hasOneReviewerRow || this.isSoleOwnerOnProof;
	}

	@action setErrorRes = (res: string): void => {
		this.errorRes = res;
	};

	private proofingStore: ProofingStore;
	private stage: Stage;

	constructor(
		proofingStore: ProofingStore,
		stage: Stage,
		permission: string,
		name?: string,
		url?: cnl,
		role?: string,
		roleUrl?: cnl,
		userMatches?: number,
		disabled?: boolean,
		id?: string,
		proofingEmailNotificationPreference?: EmailNotification,
	) {
		makeObservable(this);
		this.proofingStore = proofingStore;
		this.stage = stage;
		this.permission = permission;
		this.name = name;
		this.url = url;
		this.role = role;
		this.roleUrl = roleUrl;
		this.userMatches = userMatches;
		this.disabled = disabled;
		this.id = id;
		this.proofingEmailNotificationPreference = proofingEmailNotificationPreference;

		this.isOwner = url === proofingStore.proofOwner;
	}
}

type ValueOf<T> = T[keyof T];
type TriggerType = ValueOf<typeof STAGE_TRIGGER_TYPE>;
type CommentsTypeSetting = ValueOf<typeof STAGE_COMMENTS_OPTIONS_QUERY[number]["id"]>;
type LockStageSetting = ValueOf<typeof LOCK_STAGE_OPTIONS_QUERY>;
type FinalStageDecisionCalculationSetting = ValueOf<typeof FINAL_STAGE_DECISION_CALCULATION_OPTION_QUERY>;
type EmailNotification = ValueOf<typeof EMAIL_NOTIFICATION_OPTIONS_QUERY>;

interface StageOptions {
	name?: string;
	dueDate?: Date;
	dueDateThreshold?: number;
	finalStageDecisionCalculation?: FinalStageDecisionCalculationSetting;
	id?: string;
	stageDecisionStatus?: string;
	stageMessage?: string;
	parentStageId?: string;
	primaryDecisionMaker?: cnl;
	triggerType?: TriggerType;
	commentsTypeSetting?: CommentsTypeSetting;
	lockStage?: LockStageSetting;
	url?: string;
}

export interface Error {
	bulletPoints?: string[];
	titleResParams?: Array<number | string>;
	titleRes?: string;
	titleText?: string;
}

enum StageError {
	STAGE_NAME_EMPTY = "StageNameEmptyError",
	STAGE_NAME_NOT_UNIQUE = "StageNameNotUnique",
}

/** Generate new stage id based on old one */
function generateNewStageId(oldId: string): string {
	return (parseInt(oldId) + 1).toString();
}

export class Stage {
	readonly id: string;
	readonly previousDueDate: Date;

	@observable private _expand: boolean = false;
	@observable private _name: string;
	@observable private _nameError: StageError;
	@observable private _dueDate: Date;
	@observable private _dueDateError: string;
	@observable private _finalStageDecisionCalculation: FinalStageDecisionCalculationSetting = FINAL_STAGE_DECISION_CALCULATION_ALL_DECISIONS;
	@observable private _reviewers: Reviewer[] = [];
	@observable private _parentStage: Stage;
	@observable private _parentStageId: string;
	@observable private _primaryDecisionMaker: cnl;
	@observable private _showDueDate: boolean = false;
	@observable private _stageMessage: string = "";
	@observable private _threshold: number = DUE_DATE_NONE;
	@observable private _stageDecisionStatus: string;
	@observable private _stageTrigger: ComponentItem = STAGE_TRIGGER_OPTIONS[0];
	@observable private _stageTriggerType: TriggerType = STAGE_TRIGGER_TYPE.IMMEDIATELY;
	@observable private _commentsTypeSetting: CommentsTypeSetting = STAGE_COMMENTS_PUBLIC_ONLY;
	@observable private _lockStage: LockStageSetting = LOCK_STAGE_NEVER;
	@observable private _url: string;

	@observable assignedUserCnls = observable.set<cnl>();
	private proofingStore: ProofingStore;

	/** indicate if user chooses the specific due date option */
	get choseSpecificDueDate(): boolean {
		return this.threshold === DUE_DATE_SPECIFIC_DATE;
	}

	/* get the current comment privacy setting */
	get commentsTypeSetting(): CommentsTypeSetting {
		return this._commentsTypeSetting;
	}

	/** get the current decision calculation setting */
	get finalStageDecisionCalculationSetting(): FinalStageDecisionCalculationSetting {
		return this._finalStageDecisionCalculation;
	}

	/** return the current lock stage setting */
	get lockStageTypeSetting(): LockStageSetting {
		return this._lockStage;
	}

	/** the decision maker groups group by "group" */
	@computed get primaryDecisionMakerGroup(): Dictionary<ComponentItem[]> {
		return _.groupBy<ComponentItem>(this.primaryDecisionMakerOptions, "group");
	}

	/** return the primary decision maker */
	@computed get primaryDecisionMaker(): cnl {
		const { isStageDecisionCalculationDecisionMadeBy, primaryDecisionMakerGroup, primaryDecisionMakerPreferredGroup } = this;
		const suggestGroupObject = primaryDecisionMakerGroup[primaryDecisionMakerPreferredGroup[0]];
		const onlyHasOneSuggestedMaker = suggestGroupObject?.length === 1;

		if (onlyHasOneSuggestedMaker && !this._primaryDecisionMaker && isStageDecisionCalculationDecisionMadeBy) {
			return suggestGroupObject[0].id;
		}

		return this._primaryDecisionMaker;
	}

	/** indicates if we have any primary decision maker */
	get noPrimaryDecisionMaker(): boolean {
		return !this.primaryDecisionMaker;
	}

	/** the selected comment value or default to first (public only) */
	get commentsTypeValue(): ComponentItem {
		const { stageCommentsTypeOptions } = this.proofingStore;
		return stageCommentsTypeOptions.find((option) => option.res == this.commentsTypeSetting) || stageCommentsTypeOptions[0];
	}

	get finalStageDecisionCalculationValue(): ComponentItem {
		return this.finalStageDecisionCalculationOptions.find((option) => option.res == this.finalStageDecisionCalculationSetting) || this.finalStageDecisionCalculationOptions[0];
	}

	/** return the selected lock stage value or default to first (Never) */
	get lockStageValue(): ComponentItem {
		const { lockStageOptions } = this.proofingStore;
		return lockStageOptions.find((option) => option.res == this.lockStageTypeSetting) || lockStageOptions[0];
	}

	/** return the primary decision maker value; option.id is the CNL of the user */
	get primaryDecisionMakerValue(): ComponentItem {
		return this.primaryDecisionMakerOptions.find((option) => option.id == this.primaryDecisionMaker);
	}

	/** check if there is at least one approver */
	get atLeastOneApprover(): boolean {
		return this.reviewers.some((reviewer) => reviewer.permission === APPROVER);
	}

	get isStageDecisionCalculationDecisionMadeBy(): boolean {
		return this.finalStageDecisionCalculationSetting === FINAL_STAGE_DECISION_CALCULATION_DECISION_MADE_BY;
	}

	/** end of day, UTC */
	get endOfDay(): Date | null {
		if (this.threshold < 0) {
			return null;
		}

		const endOfDay = new Date(Date.now());
		endOfDay.setHours(23, 59, 59);
		return endOfDay;
	}

	get expand(): boolean {
		return this._expand;
	}

	/** options for due date dropdown */
	get dueDateOptions(): ComponentItem[] {
		const { isStageTriggerStartsWhen } = this;

		if (isStageTriggerStartsWhen) {
			return DUE_DATE_OPTIONS;
		} else {
			return DUE_DATE_OPTIONS.filter(({ value }) => value < 0);
		}
	}

	/** options for final stage decision calculation dropdown */
	@computed get finalStageDecisionCalculationOptions(): ComponentItem[] {
		// set decision made by unselectable when there is no approver
		return FINAL_STAGE_DECISION_CALCULATION_OPTION_QUERY
			.map((obj) => {
				if (obj.id === FINAL_STAGE_DECISION_CALCULATION_DECISION_MADE_BY) {
					return { id: obj.index, res: obj.id, disabled: this.atLeastOneApprover ? false : true };
				}
				return { id: obj.index, res: obj.id };
			});
	}

	/** the options of suggested group */
	get primaryDecisionMakerPreferredGroup(): string[] {
		return [this.proofingStore.locale.resource(FINAL_DECISION_APPROVER_RESOURCE)];
	}

	/** the options of decision maker */
	@computed get primaryDecisionMakerOptions(): ComponentItem[] {
		const { proofingStore: { decisionMakerRolePerStage, isTemplate, locale }, id } = this;
		const currentDecisionMakerRole = decisionMakerRolePerStage[id];

		// only group the results if we have set up a primary decision maker on the template, switch to "Decision made by" manually doesn't count
		if (isTemplate && xml.isValidURL(currentDecisionMakerRole)) {
			const results: ComponentItem[] = this.reviewers.filter((r) => r.permission === APPROVER && !!r.url)
				.map((r) => {
					return {
						id: r.url,
						name: r.name,
						group: locale.resource(currentDecisionMakerRole === r.roleUrl ? FINAL_DECISION_APPROVER_RESOURCE : "FinalDecisionCalculationGroupOtherApprover"),
					};
				});

			results.sort((a, b) => a.group.localeCompare(b.group));

			return results;
		}

		return this.reviewers.filter((r) => r.permission === APPROVER && r.url !== undefined)
			.map((obj) => ({ id: obj.url, name: obj.name }));
	}

	get duplicateStageName(): boolean {
		return this.stageName && this.stagesExceptThisStage.map((s) => s.stageName)
			.includes(this.stageName);
	}

	get stageName(): string {
		return this._name;
	}

	get stageNameOrBlank(): string {
		return this._name ?? "";
	}

	get stageNameError(): StageError {
		return this._nameError;
	}

	get hasStageNameError(): boolean {
		return !!this._nameError;
	}

	/** Get proof stage number - be sure to add one to the index, stage starts at 1 */
	get stageNumber(): number {
		return this.proofingStore.stages.map((s) => s.id)
			.indexOf(this.id) + 1;
	}

	get stageDecisionStatus(): string {
		return this._stageDecisionStatus;
	}

	get parentStage(): Stage {
		return this._parentStage;
	}

	/** is there a reviewer as a proof owner on any stage */
	get isAnyReviewerProofOwner(): boolean {
		return this.reviewers.some((reviewer) => reviewer.isOwner);
	}

	/** the selected stage trigger type or preselect the first one */
	get stageTriggerTypeValue(): ComponentItem {
		const { stageTriggerTypeOptions } = this.proofingStore;
		return stageTriggerTypeOptions.find((option) => option.res === this.stageTriggerType) || stageTriggerTypeOptions[0];
	}

	get parentStageId(): string {
		return this._parentStageId;
	}

	/**  return the custom message in an email notification */
	get stageMessage(): string {
		return this._stageMessage;
	}

	get stageTrigger(): ComponentItem {
		return this._stageTrigger;
	}

	get isStageTriggerStartsWhen(): boolean {
		return this.stageTrigger.value === STAGE_TRIGGER_OPTIONS[1].value;
	}

	get stageTriggerType(): string {
		return this._stageTriggerType;
	}

	get disableRemoveStageButton(): boolean {
		return this.proofingStore.hasOneStage || this.reviewers.some((r) => r.isSoleOwnerOnProof);
	}

	get dueDate(): Date {
		return this._dueDate;
	}

	get dueDateError(): string {
		return this._dueDateError;
	}

	/** this stage has one reviewer row */
	get hasOneReviewerRow(): boolean {
		return this.reviewers.length === 1;
	}

	/** indicate if this stage has any errors */
	get hasErrors(): boolean {
		return this.reviewers.some((r) => r.hasError);
	}

	/** selected threshold value for the drop down */
	get selectedThreshold(): ComponentItem {
		const { dueDateOptions, threshold } = this;
		return dueDateOptions.find((item) => item.value === threshold);
	}

	/** return whether to show the due date in the stage */
	get showDueDate(): boolean {
		return this._showDueDate;
	}

	/** threshold for due date */
	get threshold(): number {
		return this._threshold;
	}

	get reviewerDictionary(): Dictionary<Reviewer> {
		return _.keyBy<Reviewer>(this.reviewers, (x) => x.url);
	}

	get stagesExceptThisStage(): Stage[] {
		const { id, proofingStore } = this;
		const { stages } = proofingStore;
		return stages
			.filter((x) => x.id !== id);
	}

	/** Return all stages minus given stage */
	get stagesExceptThisStageAsComponentItems(): ComponentItem[] {
		const { stagesExceptThisStage } = this;
		return stagesExceptThisStage
			.map((s) => {
				const { id, stageName } = s;
				return {
					id,
					name: stageName,
				} as ComponentItem;
			});
	}

	/** options for stage trigger when stage start field is starts when. Returns all the stages except this stage and the stages that depends on it through circular dependency */
	get allowedStagesExceptThisStageAsComponentItems(): ComponentItem[] {
		return this.allowedStages.map((s) => {
			const { id, stageName } = s;
			return {
				id,
				name: stageName,
			} as ComponentItem;
		});
	}

	@computed get isStageLockedAndStarted(): boolean {
		const { cache, isEditOperation } = this.proofingStore;
		const stageNode = cache.get(this._url);
		return isEditOperation && stageNode ? stageNode.StageIsLocked && stageNode.DecisionStatus !== "ProofDecisionStatus:NotStarted" : false;
	}

	@computed get areNewAndOldDueDatesSame(): boolean {
		const oldDueDate = this.previousDueDate?.toISOString()
			.split("T")[0];
		const newDueDate = this.dueDate instanceof Date && !isNaN(this.dueDate.valueOf()) ? this.dueDate?.toISOString()
			.split("T")[0] : null;
		return oldDueDate === newDueDate;
	}

	/**
	 * Return reviewers of a stage
	 */
	get reviewers(): Reviewer[] {
		return this._reviewers;
	}

	/**
	 * When getting value for email notication, first check if the value exist in the map. If not, check its default value from user. If it is still null, then use the default, which is hourly digest
	 * @param reviewerIndex 
	 * @returns 
	 */
	getReviewerEmailNotificationValue(reviewerIndex: number): ComponentItem {
		const {
			proofingStore: {
				emailNotificationPreferenceMap,
				stageEmailNotificationsOptions,
			},
			reviewers,
		} = this;
		const { proofingEmailNotificationPreference, url } = reviewers[reviewerIndex];
		const currentNotificationPreference = emailNotificationPreferenceMap.get(url) || proofingEmailNotificationPreference || EMAIL_NOTIFICATION_DEFAULT_OPTION_NAME;
		return stageEmailNotificationsOptions.find((item) => item.res === currentNotificationPreference);
	}

	getReviewerValue(reviewerIndex: number): ComponentItem {
		const reviewer = this.reviewers[reviewerIndex];
		return { id: reviewer.url, name: reviewer.name };
	}

	getReviewerPermissionValue(reviewerIndex: number): ComponentItem {
		return PROOF_PERMISSIONS.find((item) => item.id === this.reviewers[reviewerIndex].permission) || PROOF_PERMISSIONS[0];
	}

	/* set comments type from dropdown */
	@action setCommentsType = (event: React.ChangeEvent, setting: ComponentItem): void => {
		this._commentsTypeSetting = setting.res;
	};

	/** expand or collapse this stage's accordion */
	@action setExpand = (event?: React.SyntheticEvent, expand?: boolean): void => {
		this._expand = expand || !this.expand;
	};

	@action setFinalStageDecisionCalculationType = (event: ChangeEvent, setting: ComponentItem): void => {
		this._finalStageDecisionCalculation = setting.res;
	};

	@action setLockStageType = (event: ChangeEvent, setting: ComponentItem): void => {
		this._lockStage = setting.res;
	};

	/** set the value for the primary decision maker */
	@action setPrimaryDecisionMaker = (event: ChangeEvent, setting: ComponentItem): void => {
		this._primaryDecisionMaker = setting.id;
	};

	@action setParentStageId = (id: string): void => {
		this._parentStageId = id;
	};

	@action setStageName(name: string): void {
		this._name = name;
		this.setStageNameError(!name ? StageError.STAGE_NAME_EMPTY : null);
	}

	@action setStageNameError(error: StageError): void {
		this._nameError = error;
	}

	/** Set parent (dependent) stage */
	@action setParentStage(option?: ComponentItem): void {
		if (!option) {
			this._parentStage = null;
		} else {
			this._parentStage = this.proofingStore.stagesById[option.id];
		}
	}

	@action setStageTrigger(option: ComponentItem): void {
		if (option.value === STAGE_TRIGGER.STARTSWHEN) {
			this._parentStage = this.allowedStages[0];
			this._stageTriggerType = STAGE_TRIGGER_TYPE.ON_COMPLETION;
		} else {
			// immediately case, reset to none as per Ziflow portal
			this._parentStage = null;
			if (this._threshold >= 0) {
				this._threshold = DUE_DATE_NONE;
			}
		}

		this._stageTrigger = option;
	}

	@computed get allowedStages(): Stage[] {
		const stagesAllowed = this.stagesExceptThisStage;
		const stagesNotAllowed = [];
		const parentStages = [];
		parentStages.push(this);

		// removes the circular dependency stages, so we don't end up with a cycle
		while (parentStages.length > 0) {
			const removed = parentStages.pop();
			stagesAllowed.map((stage) => {
				if (stage.parentStage === removed) {
					parentStages.push(stage);
					stagesNotAllowed.push(stage);
				}
			});
		}
		return stagesAllowed.filter((stage) => stagesNotAllowed.indexOf(stage) <= -1);
	}

	/** set message in the message textfield as the customEmailMessage */
	@action setStageMessage = (event: ChangeEvent<HTMLInputElement>): void => {
		this._stageMessage = event.target.value;
	};

	@action setStageTriggerType(option: ComponentItem): void {
		this._stageTriggerType = option.res;
	}

	/**
	 * set show due date
	 * @param option
	 * @param fromTemplate - indicate if setting from template
	 */
	@action setShowDueDate(option: ComponentItem, fromTemplate: boolean = false): void {
		if (option.value === DUE_DATE_SPECIFIC_DATE) {
			this._showDueDate = true;
			this._dueDate = this._dueDate || calculateDueDate(1);
		} else if (fromTemplate && !!this._dueDate) {
			this._showDueDate = true;
		} else {
			this._dueDate = null;
			this._showDueDate = false;
		}

		this._threshold = option.value;
	}

	@action setDueDate(dueDate: Date): void {
		if (dueDate) {
			dueDate = new Date(Date.UTC(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate(), 23, 59, 59));
			dueDate.setMinutes(dueDate.getMinutes() + dueDate.getTimezoneOffset());
		}
		this._dueDate = dueDate;
	}

	@action setDueDateError(error: string): void {
		if (!error) {
			this._dueDateError = null;
		} else if (error === "disablePast") {
			this._dueDateError = "DueDatePastError";
		} else {
			this._dueDateError = "DueDateInvalidError";
		}
	}

	@computed get isDueDateValid(): boolean {
		return !this.dueDateError && !!this.dueDate;
	}

	/** if changing or removing a reviewer permission causes no approver and it's decision made by on stage calculation, set stage calculation to All decisions */
	@action setDecisionToAll(): void {
		if (this.isStageDecisionCalculationDecisionMadeBy && !this.atLeastOneApprover) {
			this.setFinalStageDecisionCalculationType(null, FINAL_STAGE_DECISION_CALCULATION_OPTION_QUERY[0]);
		}
	}

	@action checkRemovedReviewerIsPrimaryDecisionMaker = (userCNL: cnl): void => {
		if (this.primaryDecisionMaker === userCNL) {
			this._primaryDecisionMaker = null;
		}
	};

	@action onProofPermissionSelection(reviewerIndex: number, item: ComponentItem): void {
		this._reviewers[reviewerIndex].permission = item.id;
		this.setDecisionToAll();
		this.checkRemovedReviewerIsPrimaryDecisionMaker(this._reviewers[reviewerIndex].url);
	}

	/** change the user email notification preference in the proof and set the emailNotificationPreferenceMap with a new key:value pair to reflect the changes*/
	@action onEmailNotificationSelection = (reviewerIndex: number, item: ComponentItem): void => {
		this._reviewers[reviewerIndex].proofingEmailNotificationPreference = item.res;
		this.proofingStore.emailNotificationPreferenceMap.set(this._reviewers[reviewerIndex].url, item.res);

		this.proofingStore.stages.forEach((stage) => {
			stage.reviewers.some((reviewer) => {
				if (reviewer.url === this._reviewers[reviewerIndex].url) {
					reviewer.proofingEmailNotificationPreference = item.res;
					return true;
				}
				return false;
			});
		});
	};

	/**
	 * Close warning of mulitple user matching a single role
	 */
	@action closeReviewerWarning(reviewerIndex: number): void {
		this.reviewers[reviewerIndex].userMatches = null;
	}

	@action removeReviewer = (reviewerIndex: number): void => {
		const { checkErrorOnReviewerRemoval, getDuplicateUsers, removeFromAssignedUsers, reviewers } = this;
		const removedUser = reviewers.splice(reviewerIndex, 1)[0];
		if (_.isEmpty(getDuplicateUsers(removedUser.url))) {
			removeFromAssignedUsers(removedUser.url);
		}
		this.setDecisionToAll();
		this.checkRemovedReviewerIsPrimaryDecisionMaker(removedUser.url);
		checkErrorOnReviewerRemoval(removedUser);
	};

	@action removeFromAssignedUsers = (url: cnl): void => {
		if (url) {
			this.assignedUserCnls.delete(url);
		}
	};

	/**
	 * Reviewer added using this function does not trigger form validations. This function is used for adding
	 * empty reviewer or populating form for editing workflow (existing workflow contain already validated entries).
	 * In these cases validation error cannot occur.
	 */
	@action addReviewer(permission: string, name?: string, url?: cnl, role?: string, roleUrl?: cnl, userMatches?: number, disabled?: boolean, id?: string, atIndex?: number, emailPreference?: EmailNotification): Reviewer {
		const { proofingStore } = this;
		const proofingEmailNotificationPreference = emailPreference || proofingStore.getUserEmailNotificationsPreference(url);
		const reviewer = new Reviewer(proofingStore, this, permission, name, url, role, roleUrl, userMatches, disabled, id, proofingEmailNotificationPreference);

		if (url !== null) {
			proofingStore.emailNotificationPreferenceMap.set(url, proofingEmailNotificationPreference);
		}

		if (atIndex != null) {
			this._reviewers.splice(atIndex, 0, reviewer);
		} else {
			this._reviewers.push(reviewer);
		}

		this.proofingStore.updateFormError();
		return reviewer;
	}

	/**
	 * Add all user with same role
	 */
	@action addAllMatchingUsers(reviewerIndex: number): void {
		const { bulkAddToAssignedUsers, proofingStore, reviewers } = this;
		const removedReviewer = reviewers[reviewerIndex];
		const users = proofingStore.getUsersForRole(removedReviewer.roleUrl);
		const newReviewers: Reviewer[] = users.map((u) => {
			return new Reviewer(
				proofingStore,
				this,
				removedReviewer.permission,
				u.$Name,
				u.$URL,
				removedReviewer.role,
				removedReviewer.roleUrl,
				null,
				removedReviewer.disabled,
				removedReviewer.id,
				removedReviewer.proofingEmailNotificationPreference,
			);
		});
		reviewers.splice(reviewerIndex, 1, ...newReviewers);
		bulkAddToAssignedUsers(newReviewers);
	}

	@action addBulkReviewers(users: C8Node[], role: C8Node, permission: string): void {
		const { bulkAddToAssignedUsers, proofingStore } = this;
		const newReviewers = users.map((user) => {
			const proofingEmailNotificationPreference = proofingStore.getUserEmailNotificationsPreference(user.$URL);
			return new Reviewer(
				proofingStore,
				this,
				permission,
				user.$Name,
				user.$URL,
				role.$Name,
				role.$URL,
				null,
				null,
				null,
				proofingEmailNotificationPreference,
			);
		});
		this._reviewers = [...this._reviewers, ...newReviewers];
		bulkAddToAssignedUsers(newReviewers);
	}

	@action addToAssignedUsers = (url: cnl): void => {
		if (url) {
			this.assignedUserCnls.add(url);
		}
	};

	/**
	 * Add multiple reviewer to assigned user list and check for duplicate user error
	 */
	@action bulkAddToAssignedUsers = (users: Reviewer[]): void => {
		users.map((r) => r.url)
			.forEach((url) => this.assignedUserCnls.add(url));
		this.checkUserPermission();
	};

	/** check user permission */
	@action checkUserPermission = (reviewer?: Reviewer): void => {
		const activeUsers = this.proofingStore.allActiveUsersDictionary;
		const reviewers = reviewer ? [reviewer] : this.reviewers;

		reviewers.filter((r) => r.url != null)
			.forEach((reviewer) => {
				const invalid = !activeUsers[reviewer.url];
				reviewer.permissionErrorRes = invalid ? PERMISSION_REQUIRED_ERROR_RES : "";
				reviewer.isInactive = invalid;
			});
	};

	@action onUserSelection = (reviewerIndex: number, item: ComponentItem): void => {
		const {
			addToAssignedUsers,
			checkReviewerErrorOnUserSelection,
			checkUserPermission,
			getDuplicateUsers,
			proofingStore: {
				cache,
				emailNotificationPreferenceMap,
				getUserEmailNotificationsPreference,
				proofOwner,
				userToRoleMap,
			},
			removeFromAssignedUsers,
			reviewers,
		} = this;
		const reviewer = reviewers[reviewerIndex];
		const newReviewerUrl = item.id;

		if (reviewer.url === newReviewerUrl) {
			return;
		}

		const reviewerUrlToBeRemoved = reviewer.url;

		reviewer.name = item.name;
		reviewer.url = newReviewerUrl;
		reviewer.isOwner = reviewer.url === proofOwner;

		// update the role as needed
		const rolesPerUser = userToRoleMap.get(newReviewerUrl);

		if (!rolesPerUser || rolesPerUser.length === 0) {
			reviewer.roleUrl = "";
			reviewer.role = "";
		} else {
			const roleUrl = rolesPerUser[0];
			reviewer.roleUrl = roleUrl;
			reviewer.role = cache.get(roleUrl).$Name;
		}

		// get email from existing user in the proof first. If user is a new user in the proof, retrieve it from the user default email notification preference
		reviewer.proofingEmailNotificationPreference = emailNotificationPreferenceMap.get(item.id) || getUserEmailNotificationsPreference(item.id);

		if (_.isEmpty(getDuplicateUsers(reviewerUrlToBeRemoved))) {
			removeFromAssignedUsers(reviewerUrlToBeRemoved);
		}

		addToAssignedUsers(reviewer.url);
		checkReviewerErrorOnUserSelection(reviewer);
		checkUserPermission(reviewer);
	};

	/**
	 * Each second level array contain Reviewers having same email address
	 */
	getUsersWithSameEmailInStage(): Reviewer[][] {
		const { proofingStore, reviewers } = this;
		return _.values(_.groupBy(reviewers
			.filter((r) => r.url != null), (r) => proofingStore.cache.get(proofingStore.cache.get(r.url).Details)?.Email));
	}

	/**
	* Get users to be shown in the drop-down for the given reviewer.
	* If the reviewer has a role on it, include only users from this role.
	* Otherwise, include all active users.
	* Filter out users already assigned to stages.
	* Include the reviewer itself.
	*
	* @param reviewer
	* @returns
	*/
	getDropDownUsers = (reviewer: Reviewer): ComponentItem[] => {
		let components = reviewer.roleUrl && reviewer.userMatches ?
			this.proofingStore.getUsersForRole(reviewer.roleUrl)
				.map(ProofingStore.getComponentItemFromNode) :
			this.proofingStore.allActiveUsers;

		components = components.filter((item) => item.id == reviewer.url || !this.assignedUserCnls.has(item.id));
		return components;
	};

	/**
	 * Check error on Reviewer selection
	 */
	@action checkReviewerErrorOnUserSelection = (reviewer: Reviewer): void => {
		const { proofingStore } = this;

		// On new selection, update 'same email error', if previous selection is throwing 'same email error'
		// with some other participant
		if (reviewer.errorRes === REQUIRED_USER_ERROR_RES) {
			reviewer.errorRes = null;
		} else if (reviewer.errorRes === USER_WITH_SAME_EMAIL_ERROR_RES) {
			proofingStore.checkUsersWithSameEmail();
		}

		//If current selection is not a duplicate , check and update 'same email error' if SameEmailAddressError
		//error is currenlty being displayed on the form.
		if (!reviewer.isDuplicate && proofingStore.formError?.titleRes === "SameEmailAddressError") {
			proofingStore.checkUsersWithSameEmail();
		}
	};

	@action checkErrorOnReviewerRemoval = (reviewer: Reviewer): void => {
		const { getDuplicateUsers, proofingStore } = this;
		if (!reviewer.url) {
			return;
		}

		if (reviewer.isDuplicate) {
			const duplicateUsers = getDuplicateUsers(reviewer.url);

			// If there is only one other duplicate, remove error from other participant
			if (duplicateUsers.length == 1) {
				duplicateUsers[0].errorRes = null;
			}
		} else if (reviewer.errorRes === USER_WITH_SAME_EMAIL_ERROR_RES) {
			proofingStore.checkUsersWithSameEmail();
		}
	};

	/**
	 * Check error in a Stage
	 */
	@action checkErrorInStage(isEditOperation: boolean): boolean {
		const { areNewAndOldDueDatesSame, duplicateStageName, reviewers, stageName } = this;
		let emptyUserSelection: Reviewer[];
		let duplicateUsers: Reviewer[];

		// First check for empty stage names, then for duplicates, then for empty user selection, then for duplicate users
		let stageNameError = null;
		if (!stageName) {
			stageNameError = StageError.STAGE_NAME_EMPTY;
		} else if (duplicateStageName) {
			stageNameError = StageError.STAGE_NAME_NOT_UNIQUE;
		}

		this.setStageNameError(stageNameError);

		const isStageNameError = !!stageNameError;
		if ((emptyUserSelection = reviewers.filter((r) => !r.url)).length) {
			emptyUserSelection.forEach((r) => r.errorRes = REQUIRED_USER_ERROR_RES);
			return true;
		} else if (this.dueDateError) {
			return isStageNameError || !isEditOperation || !areNewAndOldDueDatesSame;
		}
		return isStageNameError;
	}

	getDuplicateUsers = (url: cnl): Reviewer[] => {
		return url ? this._reviewers.filter((r) => r.url === url) : [];
	};

	constructor(proofingStore: ProofingStore, stageOptions: StageOptions) {
		makeObservable(this);

		const {
			dueDate,
			dueDateThreshold,
			finalStageDecisionCalculation,
			id,
			name,
			parentStageId,
			primaryDecisionMaker,
			stageMessage,
			stageDecisionStatus,
			lockStage,
			triggerType,
			commentsTypeSetting,
			url,
		} = stageOptions;

		this.id = id;
		this.proofingStore = proofingStore;
		this._name = name;
		this._dueDate = dueDate;
		// Some attributes in stageOption are null and will overwrite the existing values when the stage is created for the first time, so I need to not overwrite the values if they are null.
		this._finalStageDecisionCalculation = finalStageDecisionCalculation || this._finalStageDecisionCalculation;
		this._primaryDecisionMaker = primaryDecisionMaker || this._primaryDecisionMaker;
		this._showDueDate = !!dueDate;
		this._stageDecisionStatus = stageDecisionStatus;
		this._stageMessage = stageMessage || this._stageMessage;

		if (triggerType && triggerType !== STAGE_TRIGGER_TYPE.IMMEDIATELY) {
			this._parentStageId = parentStageId;
			this._stageTrigger = STAGE_TRIGGER_OPTIONS[1];
			this._stageTriggerType = triggerType;
		}
		this._commentsTypeSetting = commentsTypeSetting;
		this._lockStage = lockStage || this._lockStage;
		this.previousDueDate = dueDate;
		this._reviewers = [];
		this._url = url;

		if (dueDateThreshold != null) {
			this._threshold = dueDateThreshold;
		}
	}
}

export default class ProofingStore extends ReactionStore {
	@observable.ref private _activeTemplates: C8Node[];
	@observable.ref private _allActiveUsers: C8Node[];
	@observable.ref private _proofAdminUsers: C8Node[] = [];
	@observable private _proofOwner: cnl;
	@observable _formError: Error;
	@observable private _readyToShowStages: boolean;
	@observable private _isSelectProofOwnerDialogOpen: boolean = false;
	@observable private _selectedWorkflow: ComponentItem;
	@observable private _selectedTemplate: ComponentItem;
	@observable private _stages: Stage[] = [];
	@observable private _userEmailNotificationPreferenceMap: Map<cnl, EmailNotification> = new Map();
	@observable cache: WorkingCache;
	@observable isEditOperation: boolean;
	private documentCnl: cnl;
	private previousProofVersionId: string;
	private isCreatedByUserProofAdmin: boolean = false;
	private selectedProofOwnerFromLegacyCnl: cnl;
	private _roleToUserMap = new Map<cnl, C8Node[]>();
	private _userToRoleMap = new Map<cnl, cnl[]>();
	private _crewUrl: cnl;
	private _decisionMakerRolePerStage: Record<string, cnl> = {};
	private _isApprovedWithChangesDisabled: boolean = false;

	readonly locale: ILocaleProvider;

	get allUniqueReviewersOnProof(): Set<cnl> {
		return new Set(this.stages.flatMap((s) => s.reviewers.map((r) => r.url)));
	}

	get decisionMakerRolePerStage(): Record<string, cnl> {
		return this._decisionMakerRolePerStage;
	}

	/** indicate if the current one is using template */
	get isTemplate(): boolean {
		return this.selectedWorkflow.id === USE_TEMPLATE;
	}

	get isSelectProofOwnerDialogOpen(): boolean {
		return this._isSelectProofOwnerDialogOpen;
	}

	get selectedProofOwnerFromLegacy(): C8Node {
		return this.cache.get(this.selectedProofOwnerFromLegacyCnl);
	}

	/**
	 * Get the list of users for the given role
	 *
	 * @param roleUrl - CNL of the role
	 * @returns the list of users
	 */
	getUsersForRole = (roleUrl: cnl): C8Node[] => {
		return this._roleToUserMap.get(roleUrl) ?? [];
	};

	/**
	 * Return active proof workflow templates
	 */
	@computed get activeTemplates(): ComponentItem[] {
		return (this._activeTemplates ?? []).map(ProofingStore.getComponentItemFromNode);
	}

	/**
	 * Return users who have Proof Administrator role
	 */
	get proofAdminUsers(): ComponentItem[] {
		return this._proofAdminUsers.map(ProofingStore.getComponentItemFromNode);
	}

	get proofAdminUsersDictionary(): Dictionary<ComponentItem> {
		return _.keyBy<ComponentItem>(this.proofAdminUsers, (x) => x.id);
	}

	get proofOwnerValue(): ComponentItem | null {
		return this.proofAdminUsersDictionary[this.proofOwner];
	}

	get proofOwner(): cnl {
		return this._proofOwner;
	}

	get isApprovedWithChangesDisabled(): boolean {
		return this._isApprovedWithChangesDisabled;
	}

	get document(): C8Node {
		return this.cache.get(this.documentCnl);
	}

	get documentRevision(): C8Node {
		return this.cache.get(this.document.CurrentRevision);
	}

	get currentProofWorkflow(): C8Node {
		return this.cache.get(this.documentRevision.ProofWorkflow);
	}

	// Proofing Migration -------------------------------------------------------------------------------------------------------
	// currentProofWorkflow doesn't exist when editing / starting proof that's been revised
	get createdByUserExistsOnAnyStage(): boolean {
		if (!this.currentProofWorkflow) {
			return false;
		}
		const { Participants, $CR } = this.currentProofWorkflow;
		return Participants.includes($CR);
	}

	// return the value of particular attribute of this or the last proof workflow from the previous revision
	getThisOrLastProofWorkflowProperty<T>(property: string): T | null {
		const pwNodes = this.cache.getPathNodes(this.documentCnl, "Child:Revisions/Child:ProofWorkflow");
		const { length } = pwNodes;
		if (length) {
			return pwNodes[length - 1][property];
		}
		return null;
	}

	/** proof created by cnl */
	@computed get createdByCnl(): cnl | null {
		return this.getThisOrLastProofWorkflowProperty<cnl>("$CR");
	}

	/** get whether this proof is legacy */
	@computed get legacyProof(): boolean | null {
		return this.getThisOrLastProofWorkflowProperty<boolean>("LegacyProof");
	}

	get selectCreatedByUserAsProofOwner(): boolean {
		return this.legacyProof && this.isCreatedByUserProofAdmin && this.createdByUserExistsOnAnyStage;
	}

	get selectCreatedByUserAsProofOwnerAndAddUserToFirstStageAsViewer(): boolean {
		return this.legacyProof && this.isCreatedByUserProofAdmin && !this.createdByUserExistsOnAnyStage && !!this.allActiveUsersDictionary[this.createdByCnl];
	}

	get showSelectProofOwnerDialog(): boolean {
		return this.legacyProof && (!this.isCreatedByUserProofAdmin || !this.allActiveUsersDictionary[this.createdByCnl]);
	}

	@action setIsSelectProofOwnerDialogOpen(open: boolean): void {
		this._isSelectProofOwnerDialogOpen = open;
	}

	onChangeProofOwnerFromLegacy = (event: ChangeEvent<{}>, value: ComponentItem, reason: AutocompleteChangeReason): void => {
		this.selectedProofOwnerFromLegacyCnl = value.id;
	};

	onCloseSelectNewProofOwnerFromLegacy = (): void => {
		this.setIsSelectProofOwnerDialogOpen(false);
	};
	// ---------------------------------------------------------------------------------------------------------------------------

	@action setProofOwner(newOwner: cnl): void {
		const oldOwner = this.proofOwner;
		this._proofOwner = newOwner;
		this.replaceProofOwnerOnReviewersWhereExists(oldOwner, newOwner);
	}

	/** replace proof owner on all reviewers where applicable */
	@action replaceProofOwnerOnReviewersWhereExists(oldOwner: cnl, newOwner: cnl): void {
		for (const stage of this.stages) {
			for (const [index, reviewer] of stage.reviewers.entries()) {
				if (reviewer.url === oldOwner && !stage.reviewerDictionary[newOwner]) {
					stage.removeReviewer(index);
					stage.addReviewer(
						reviewer.permission,
						this.proofAdminUsersDictionary[newOwner].name,
						newOwner,
						reviewer.role,
						reviewer.roleUrl,
						reviewer.userMatches,
						reviewer.disabled,
						newOwner,
						index,
						reviewer.proofingEmailNotificationPreference,
					);
				} else if (reviewer.url === newOwner) {
					reviewer.isOwner = true;
				} else {
					reviewer.isOwner = false;
				}
			}
		}
	}

	get uniqueStageIds(): Set<string> {
		return new Set(this.stages.map((x) => x.id));
	}

	get formError(): Error {
		return this._formError;
	}

	/** indicate all stages have been instantiated */
	get readyToShowStages(): boolean {
		return !!this.proofOwner && this._readyToShowStages;
	}

	/**
	 * Return current workflow selection
	 */
	get selectedWorkflow(): ComponentItem {
		return this._selectedWorkflow;
	}

	/**
	 * Return all active users
	 */
	get allActiveUsers(): ComponentItem[] {
		return (this._allActiveUsers ?? []).map(ProofingStore.getComponentItemFromNode);
	}

	/** user dictionary */
	get allActiveUsersDictionary(): Dictionary<ComponentItem> {
		return _.keyBy<ComponentItem>(this.allActiveUsers, (x) => x.id);
	}

	/** User nodes dictionary with URL keys */
	get allActiveUsersNodesDictionary(): Dictionary<C8Node> {
		return _.keyBy<C8Node>(this._allActiveUsers, (x) => x.$URL);
	}

	/** get stage email notifications options */
	@computed get stageEmailNotificationsOptions(): ComponentItem[] {
		return EMAIL_NOTIFICATION_OPTIONS_QUERY.map((obj) => ({ id: obj.index, res: obj.id }));
	}

	/** get stage trigger type options */
	@computed get stageTriggerTypeOptions(): ComponentItem[] {
		return STAGE_TRIGGER_TYPE_OPTIONS_QUERY.map((obj) => { return { id: obj.index, res: obj.id }; })
			.slice(3)
			.reverse()
			.filter((x) => !(this.isApprovedWithChangesDisabled && x.res === STAGE_TRIGGER_TYPE.ON_APPROVED_WITH_CHANGES));
	}

	/** get the lock stage options */
	@computed get lockStageOptions(): ComponentItem[] {
		return LOCK_STAGE_OPTIONS_QUERY.map((obj) => ({ id: obj.index, res: obj.id }));
	}

	/** get stage comments type options */
	@computed get stageCommentsTypeOptions(): ComponentItem[] {
		return STAGE_COMMENTS_OPTIONS_QUERY.map((obj) => ({ id: obj.index, res: obj.id }));
	}

	/** get stage trigger options */
	@computed get stageTriggerOptions(): ComponentItem[] {
		let immediatelyCount = 0;
		const allowStageTrigger = this.stages.some((stage) => {
			if (!stage.isStageTriggerStartsWhen) {
				immediatelyCount++;
			}
			return immediatelyCount > 1;
		});
		if (allowStageTrigger) {
			return STAGE_TRIGGER_OPTIONS.map((option) => { return { ...option, disabled: false }; });
		}
		return STAGE_TRIGGER_OPTIONS;
	}

	/**
	 * returns a map that stores all the users and their corresponding email notification preference in the proof
	 */
	@computed get emailNotificationPreferenceMap(): Map<cnl, EmailNotification> {
		return this._userEmailNotificationPreferenceMap;
	}

	/**
	 * Return current template selection
	 */
	get selectedTemplate(): ComponentItem {
		return this._selectedTemplate;
	}

	get stagesById(): Dictionary<Stage> {
		return _.keyBy<Stage>(this.stages, (stage) => stage.id);
	}

	get stagesByName(): Dictionary<Stage> {
		return _.keyBy<Stage>(this.stages, (stage) => stage.stageName);
	}

	/** return whether this proof has one stage */
	get hasOneStage(): boolean {
		return this.stages.length === 1;
	}

	/**
	 * Return all stages
	 */
	get stages(): Stage[] {
		return this._stages;
	}

	/** is the current user a valid proof approver */
	get activeValidUser(): ComponentItem | undefined {
		return this.allActiveUsersDictionary[sessionStore.userUrl];
	}

	/** indicate if there're any errors on stages */
	get hasErrorOnStage(): boolean {
		return _.some(this.stages, (stage) => stage.hasErrors);
	}

	/** the map of roles per user */
	get userToRoleMap(): Map<cnl, cnl[]> {
		return this._userToRoleMap;
	}

	/**
	 * Checks if Stage can be added in the workflow
	 */
	allowAddStage = (isEditOperation: boolean): boolean => {
		return (this.selectedWorkflow?.id === USE_CUSTOM && !isEditOperation) ||
			(isEditOperation && this.stages.length > 0) ||
			(this.selectedWorkflow?.id === USE_TEMPLATE && (this.stages.length > 0));
	};

	onSaveSelectNewProofOwnerFromLegacy = (): void => {
		const stage = this.stages[0];
		const { $URL, $Name } = this.selectedProofOwnerFromLegacy;
		if (!this.allUniqueReviewersOnProof.has($URL)) {
			stage.addReviewer(VIEWER, $Name, $URL);
			stage.addToAssignedUsers($URL);
		}
		this.setProofOwner($URL);
		this.setIsSelectProofOwnerDialogOpen(false);
	};

	@action setWorkflow(item: ComponentItem, isEditOperation: boolean): void {
		if (item.id == this._selectedWorkflow?.id) {
			// this assignment will just render workflow selection dropdown on selecting 'Cancel' option from workflow selection confirmation dialog.
			this._selectedWorkflow = item;
		} else {
			this._selectedWorkflow = item;
			this._stages = [];
			if (!this.selectCreatedByUserAsProofOwner && !this.selectCreatedByUserAsProofOwnerAndAddUserToFirstStageAsViewer) {
				this.setProofOwner(sessionStore.userUrl);
			}

			if (this._selectedWorkflow.id == USE_CUSTOM) {
				this._readyToShowStages = false;
				this.setTemplate(null);
				if (!isEditOperation) {
					this.addStageWithOneReviewer(true);
				}
			}

			// for the template case, we can be able to show all the stages, may need to change after we done with refactoring the template settings
			this._readyToShowStages = true;
		}
	}

	@action setFormError(error: Error): void {
		this._formError = error;
	}

	@action setTemplate(item: ComponentItem): void {
		if (item === this._selectedTemplate) {
			return;
		}

		this._selectedTemplate = item;
		this.clearStages();

		this.buildStagesFromTemplate();
	}

	/** calculate the values of attributes based on the template */
	private processTemplate = (template: C8Node, options: StageOptions): void => {
		const isImmediate = isImmediately(template);

		const {
			DueDateThreshold,
			ParentStage,
		} = template;

		if (isImmediate) {
			options.dueDateThreshold = DueDateThreshold != -1 ? DUE_DATE_SPECIFIC_DATE : DueDateThreshold;
			options.dueDate = calculateDueDate(DueDateThreshold);
		} else {
			options.dueDateThreshold = DueDateThreshold;
			const parentStage = this.cache.get(ParentStage);

			// handle the parent stage
			options.parentStageId = parentStage.$Name;
		}
	};

	@action private buildStagesFromTemplate(): void {
		const {
			_activeTemplates,
			addStage,
			cache,
			getUsersForRole,
			processTemplate,
			proofAdminUsersDictionary,
			proofOwner,
			selectedTemplate: item,
			stages,
		} = this;

		if (!item) {
			return;
		}

		const template = _activeTemplates.find((t) => t.$URL == item.id);

		if (!template) {
			console.error("Cannot find template with URL=", item.id);
			return;
		}

		for (const templateStageUrl of template.Stages) {
			const templateStage = cache.get(templateStageUrl);
			const {
				CommentsType,
				FinalDecisionCalculation,
				LockStage,
				StageMessage,
				TriggerType,
				PrimaryDecisionMaker,
			} = templateStage;

			const stageOptions: StageOptions = {
				commentsTypeSetting: CommentsType,
				finalStageDecisionCalculation: FinalDecisionCalculation,
				lockStage: LockStage,
				stageDecisionStatus: PROOF_DECISION_STATUS.NOT_STARTED,
				stageMessage: StageMessage,
				triggerType: TriggerType,
			};

			// handle the dependent properties
			processTemplate(templateStage, stageOptions);

			// handle the normal properties
			const stage = addStage(stageOptions);

			// store the decision maker role 
			this._decisionMakerRolePerStage[stage.id] = PrimaryDecisionMaker;

			stage.setStageName(templateStage.$Name);
			stage.setShowDueDate(DUE_DATE_OPTIONS.find(({ value }) => value === stageOptions.dueDateThreshold), true);

			for (const stageConfigUrl of templateStage.StageConfigs) {
				const stageConfig = cache.get(stageConfigUrl);
				const roleCnl = stageConfig.Role;
				const role = cache.get(roleCnl);
				const users = getUsersForRole(roleCnl);

				if (users.length == 0 || users.length > 5) {
					stage.addReviewer(stageConfig.TaskDefinition, null, null, role.$Name, role.$URL, users.length);
				} else {
					stage.addBulkReviewers(users, role, stageConfig.TaskDefinition);
				}
			}
		}

		let isProofOwnerOnAnyStage = false;
		// lastly, populate the parent stages 
		stages.forEach((stage) => {
			const { parentStageId } = stage;

			if (parentStageId) {
				const stageInstance = this.stagesByName[parentStageId];
				stage.setParentStage(stageInstance);
				stage.setParentStageId(stageInstance.id);
			}

			if (!isProofOwnerOnAnyStage && stage.isAnyReviewerProofOwner) {
				isProofOwnerOnAnyStage = true;
			}
		});

		// if the proof owner (current user) does not exist on any stages, add them as approver on the first stage
		if (!isProofOwnerOnAnyStage) {
			stages[0].addReviewer(
				APPROVER,
				proofAdminUsersDictionary[proofOwner].name,
				proofOwner,
				null,
				null,
				null,
				null,
				null,
				0,
			);
		}
	}

	@action addStage = (stageOptions: StageOptions = {}): Stage => {
		const { stages } = this;
		let { id } = stageOptions;
		id ||= (stages.length + 1).toString();
		while (this.uniqueStageIds.has(id)) {
			id = generateNewStageId(id);
		}
		const stage = new Stage(this, { ...stageOptions, id });
		this.stages.push(stage);
		this.updateFormError();
		return stage;
	};

	/** add a stage with one viewer */
	@action addStageWithViewer(viewerCnl: cnl): void {
		const viewerNode = this.cache.get(viewerCnl);
		const stage = this.addStage();
		const { $URL, $Name } = viewerNode;
		stage.addReviewer(VIEWER, $Name, $URL);
		stage.addToAssignedUsers($URL);
	}

	/** add a stage with one reviewer, option to initialize with current user */
	@action addStageWithOneReviewer(initWithCurrentUser?: boolean): void {
		const stage = this.addStage();
		const { activeValidUser } = this;
		if (initWithCurrentUser && activeValidUser) {
			stage.addReviewer(APPROVER, activeValidUser.name, activeValidUser.id);
			stage.addToAssignedUsers(activeValidUser.id);
		} else {
			stage.addReviewer(REVIEWER);
		}
	}

	/** remove stage and set all dependent parent stage numbers back one or reset to immediate (no trigger) */
	@action removeStage(stageIndex: number): void {
		const { _stages } = this;
		for (const s of _stages) {
			if (s.parentStage?.stageNumber === stageIndex) {
				s.setStageTrigger(STAGE_TRIGGER_OPTIONS[0]);
				s.setParentStage(null);
			}
		}
		_stages.splice(stageIndex - 1, 1);
		this.updateFormError();
	}

	@action clearStages(): void {
		this._stages = [];
	}

	async setIsApprovedWithChangesDisabled(): Promise<void> {
		const res = await fetchIsApprovedWithChangesDisabled();
		this._isApprovedWithChangesDisabled = res.getReturnValue();
	}

	/**
	 * Fetch active users together with UserDetails
	 */
	fetchActiveUsers(adminOnly?: boolean): Promise<JsonResponse> {
		return fetchUsersWithProofingRoles(adminOnly, this.cache, this._crewUrl, (nodes) => {
			runInAction(() => {
				if (adminOnly) {
					this._proofAdminUsers = nodes;
				} else {
					this._allActiveUsers = nodes;
				}
			});
		});
	}

	/**
	 * Fetch active proof workflow templates
	 */
	private fetchActiveTemplates(): Promise<JsonResponse> {
		const search = getSearch("ProofTemplate.Active");
		search.addDependencyPaths([pathCreate("Stages", "StageConfigs", "Role")]);

		return runSearch(search, this.cache, (nodes) => {
			runInAction(() => {
				this._activeTemplates = nodes;
			});
		});
	}

	/**
	 * Recheck and update no stage or stage with no user error, if they already exist.
	 */
	@action updateFormError = (): void => {
		if (this.formError) {
			this.hasNoStageOrEmptyStageError();
		}
	};

	/**
	 * Checks for no stage or stage with no user
	 */
	@action hasNoStageOrEmptyStageError = (): boolean => {
		if (!this.stages.length) {
			this.setFormError({ titleRes: "NoStageOrEmptyStageError" });
			return true;
		} else if (this.stages.find((s) => s.reviewers.length === 0)) {
			this.setFormError({ titleRes: "CannotHaveStageWithoutUserError" });
			return true;
		} else if (_.includes(["NoStageOrEmptyStageError", "CannotHaveStageWithoutUserError"], this.formError?.titleRes)) {
			this.setFormError(null);
			return false;
		}
	};

	@action checkUsersWithSameEmail = (): boolean => {
		let errorMessage: Error = null;
		const sameEmailUsers = _.flatten(this.stages.map((s) => s.getUsersWithSameEmailInStage()));

		const userNames: string[] = [];
		sameEmailUsers.forEach((rs) => {
			if (rs.length === 1) {
				if (rs[0].errorRes === USER_WITH_SAME_EMAIL_ERROR_RES) {
					rs[0].errorRes = null;
				}
			} else {
				const names = rs.map((u) => {
					u.errorRes = USER_WITH_SAME_EMAIL_ERROR_RES;
					return u.name;
				});
				userNames.push(names.splice(0, names.length - 1)
					.join(", ") + " and " + _.last(names));
			}
		});

		if (userNames.length) {
			errorMessage = {
				titleRes: "SameEmailAddressError",
				bulletPoints: userNames.map((u) => locale.resource("SameEmailAddressUsers", [u])),
			};
		}
		this.setFormError(errorMessage);
		return errorMessage != null;
	};

	/**
	 * Checks if the final stage decision calculation is decision made by and if user already selected a primary decision maker
	 * if not, expand the dialog if it is closed and display an error message
	 */
	@action checkNoPrimaryDecisionMaker = (): boolean => {
		let error = false;
		this.stages.forEach((s) => {
			if (s.isStageDecisionCalculationDecisionMadeBy && !s.primaryDecisionMaker) {
				s.setExpand(null, true);
				error = true;
			}
		});
		return error;
	};

	/**
	 * Checks complete form for errors
	 */
	@action validateForm = (isEditOperation: boolean): boolean => {
		/**
		 * Order of error checking:
		 * 1. Checking for No Stage or Stage with no user
		 * 2. Checking for Missing user selection and Duplicate users
		 * 3. Checking for Different user having same email
		 * 4. Checking for No Approver selected for primary decision maker
		 */
		return !(this.hasNoStageOrEmptyStageError() ||
			(this.stages ?? []).map((s) => s.checkErrorInStage(isEditOperation))
				.find(Boolean) != undefined ||
			this.checkUsersWithSameEmail() ||
			this.checkNoPrimaryDecisionMaker());
	};


	/**
	 * Fetch mapping between roles and users in the context of the current DocumentRevision
	 */
	private async fetchRoleUserMapping(crewUrl: cnl): Promise<void> {
		// collect all roles from all templates
		const roleCnls = new Set<cnl>();
		for (const template of this._activeTemplates) {
			for (const templateStageUrl of template.Stages) {
				const templateStage = this.cache.get(templateStageUrl);

				for (const stageConfigUrl of templateStage.StageConfigs) {
					const stageConfig = this.cache.get(stageConfigUrl);
					const roleCnl = stageConfig.Role;
					roleCnls.add(roleCnl);
				}
			}
		}

		// get CrewAssignmentReport-s for roles from templates
		const search = new Search({
			Module: "UserProvider",
			Operation: "CrewAssignmentReportForRoles",
			Crew: crewUrl,
			Roles: Array.from(roleCnls),
			IncludeGlobal: true,
			IsWorkflow: true,
			ClosestOnly: true,
		});

		const res: JsonResponse = await search.getRequest()
			.go();
		runInAction(() => this.cache.cacheResponse(res));

		// convert CrewAssignmentReport-s into role->users map
		this._roleToUserMap.clear();
		res.getResultNodes()
			.forEach((n) => {
				const roleCnl = n.Role;
				const userCnl = n.User;
				const user = this.cache.get(userCnl);
				if (user.Active) {
					let userList = this._roleToUserMap.get(roleCnl);
					let roleList = this._userToRoleMap.get(userCnl);

					if (!userList) {
						userList = [];
						this._roleToUserMap.set(roleCnl, userList);
					}

					if (!roleList) {
						roleList = [];
						this._userToRoleMap.set(userCnl, roleList);
					}

					roleList.push(roleCnl);
					userList.push(user);
				}
			});
	}

	/**
	 * Populate form for editing proof workflow
	 */
	populateForm = async (currentDocumentRevision: C8Node, createProofRevision?: boolean): Promise<void> => {
		const currentWorkflow = this.cache.get(currentDocumentRevision.ProofWorkflow);

		if (createProofRevision) {
			this.previousProofVersionId = currentWorkflow.Id;
		}

		// not ready to show stages until parent (dependent) stages are added
		runInAction(() => {
			if (this.selectCreatedByUserAsProofOwner || this.selectCreatedByUserAsProofOwnerAndAddUserToFirstStageAsViewer) {
				this._proofOwner = this.createdByCnl;
			} else {
				this._proofOwner = currentWorkflow.ProofOwner;
			}
			this._readyToShowStages = false;
		});
		for (const [index, subProcessUrl] of currentWorkflow.Subprocesses.entries()) {
			const subProcess = this.cache.get(subProcessUrl);
			const parentStageId = this.cache.get(subProcess.ParentStage)?.Id;
			const stageOptions: StageOptions = {
				commentsTypeSetting: subProcess.CommentsType,
				dueDate: !createProofRevision && subProcess.DueDate ? new Date(subProcess.DueDate) : null,
				dueDateThreshold: subProcess.DueDateThreshold,
				finalStageDecisionCalculation: subProcess.FinalStageDecisionCalculation,
				id: subProcess.Id,
				lockStage: subProcess.LockStage,
				name: subProcess.$Name,
				stageMessage: subProcess.StageMessage,
				parentStageId,
				primaryDecisionMaker: subProcess.PrimaryDecisionMaker,
				stageDecisionStatus: subProcess.DecisionStatus,
				triggerType: subProcess.TriggerType,
				url: subProcessUrl,
			};
			const stage = this.addStage(stageOptions);

			// set the due date through the action so that that other properties would be set accordingly along with it
			stage.setShowDueDate(DUE_DATE_OPTIONS.find(({ value }) => value === stageOptions.dueDateThreshold), false);

			if (index === 0 && this.selectCreatedByUserAsProofOwnerAndAddUserToFirstStageAsViewer) {
				const assignee = this.cache.get(currentWorkflow.$CR);
				stage.addReviewer(VIEWER, assignee.$Name, assignee.$URL);
				stage.addToAssignedUsers(assignee.$URL);
			}

			for (const taskUrl of subProcess.Tasks) {
				const task = this.cache.get(taskUrl);
				const assignee = this.cache.get(task.Assignee);
				const disabled = !createProofRevision && !["ProofDecisionStatus:NotStarted", "ProofDecisionStatus:DecisionPending", "ProofDecisionStatus:InProgress", "ProofDecisionStatus:ViewOnly"].includes(task.DecisionStatus);
				stage.addReviewer(task.TaskDefinition, assignee.$Name, assignee.$URL, null, null, null, disabled, task.Id, null, task.EmailNotificationPreference);
				stage.addToAssignedUsers(assignee.$URL);
			}
		}

		// after all stages are added, add parent Stage class from ref
		this.stages.forEach((stage) => {
			if (stage.parentStageId) {
				stage.setParentStage(this.stagesById[stage.parentStageId]);
			}
		});

		if (this.showSelectProofOwnerDialog) {
			this.setIsSelectProofOwnerDialogOpen(true);
			this.selectedProofOwnerFromLegacyCnl = this.proofAdminUsers[0].id;
		}

		runInAction(() => {
			this._readyToShowStages = true;
		});
	};

	@action getPreviousDocRevisionWithProof = async (document: C8Node): Promise<C8Node> => {
		const revisions = await resolveNodes(document.Revisions, this.cache);
		return revisions.reverse()
			.find((n) => n.ProofWorkflow != EMPTY_URL);
	};

	/**
	 * Gets a user's email notification preference from the user's url, or return the EMAIL_NOTIFICATION_DEFAULT_OPTION_NAME if user not found
	 */
	getUserEmailNotificationsPreference = (url: string): EmailNotification => {
		const user = this.allActiveUsersNodesDictionary[url];
		return this.cache.get(user?.Details)?.ProofingEmailNotificationPreference ?? EMAIL_NOTIFICATION_DEFAULT_OPTION_NAME;
	};

	/**
	 * Checks if template is edited incase of custom template selection
	 */
	isCustomTemplateEdited = (): boolean => {
		const { stages } = this;
		return stages.length && (stages.length > 1 || stages[0].stageName != null || stages[0].dueDate != null || stages[0].assignedUserCnls.size > 0);
	};

	buildRequest = (currentRevision: C8Node, isEditOperation: boolean): PostRequest => {
		const { previousProofVersionId, proofOwner } = this;
		const stages = this.stages.map((s, i) => {
			const stage = {
				id: s.id,
				name: s.stageName,
				dueDate: s.dueDate,
				finalStageDecisionCalculation: s.finalStageDecisionCalculationSetting,
				reviewers: s.reviewers.map((r) => { return { ...r, proofingStore: null, stage: null }; }),
				stageTriggerType: s.stageTriggerType,
				stageMessage: s.stageMessage,
				lockStage: s.lockStageTypeSetting,
				threshold: s.threshold,
				endOfDay: s.endOfDay,
				parentStageId: null,
				parentStageName: null,
				primaryDecisionMaker: s.primaryDecisionMaker,
				stageCommentsType: s.commentsTypeSetting,
			};
			if (s.parentStage) {
				stage.parentStageName = s.parentStage.stageName;
				stage.parentStageId = s.parentStage.id;
			}
			return stage;
		});

		if (isEditOperation) {
			return new PostRequest("Proofing", "EditProof")
				.addParameter("URL", currentRevision.ProofWorkflow)
				.addParameter("EditProofOptions", JSON.stringify({
					proofOwner,
					stages,
				}));
		} else {
			return new PostRequest("Proofing", "CreateProof")
				.addParameter("documentRevision", currentRevision.$URL)
				.addParameter("CreateProofRequest", JSON.stringify({
					previousProofVersionId,
					proofOwner,
					stages,
				}));
		}
	};

	static getComponentItemFromReviewer(reviewer: Reviewer): ComponentItem {
		return { name: reviewer.name, id: reviewer.url };
	}

	static getComponentItemFromNode(node: C8Node): ComponentItem {
		return { name: node.$Name, id: node.$URL };
	}

	async setIsCreatedByUserProofAdmin(document: C8Node): Promise<void> {
		if (this.createdByCnl) {
			this.isCreatedByUserProofAdmin = (await fetchIsUserProofAdmin(this.createdByCnl, document.CurrentRevision)).getReturnValue();
		}
	}

	constructor(document: C8Node, loc: ILocaleProvider, isEditOperation: boolean, cache: WorkingCache) {
		super();
		makeObservable(this);
		this.cache = cache;
		this.documentCnl = document.$URL;
		const crewUrl = document[xml.ATTR_CREW];
		this._crewUrl = crewUrl;
		this.locale = loc;
		this.isEditOperation = isEditOperation;
		const templatesPromise = this.fetchActiveTemplates();
		this.setIsApprovedWithChangesDisabled();
		this.setIsCreatedByUserProofAdmin(document);

		// role->user mapping requires the knowledge about template roles
		templatesPromise.then(() => this.fetchRoleUserMapping(crewUrl));
	}
}

export const ProofingStoreContext = createContext<ProofingStore>(undefined);
export const useProofingStore = (): ProofingStore => useContext(ProofingStoreContext);